import React, { useState, useEffect, useCallback } from "react";
import { formatAmount, formatDigits } from "../../../Utils/formatAmount";
import { formatDateTime, dateConvert } from "../../../Utils/ConvertDate";
import { RightAlignedContainer } from "../../../Utils/styledComponents";
import { AccountingDateFilter } from "../../../Utils/AccountingUtils/AccountingDateFilter";
import axios from "axios";
import config from "../../../Helpers/config.json";
import { bindActionCreators } from "redux";
import * as snackbarActions from "../../../Redux/Actions/SnackbarActions/SnackbarActions";
import { connect } from "react-redux";
import { MuiPagination } from "../../../Utils/Tables/CustomTable";
import CustomDialog from "../../../Utils/Dialog/CustomDialog";
import { usePrint } from "../../../Utils/Template/usePrint";
import { useExcelReports, usePdfReports } from "../../Invoice/CustomHooks/useExcelReports";
import { SpecificJournalTemplate } from "./SpecificJournal";

// ────────────────────────────────────────────────
//            PROFESSIONAL STYLES
// ────────────────────────────────────────────────
const styles = {
  container: {
    padding: "2rem 1.5rem",
    maxWidth: "1480px",
    margin: "0 auto",
    background: "#ffffff",
    borderRadius: "12px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "1.8rem",
    flexWrap: "wrap",
    gap: "1rem",
  },
  title: { margin: 0, fontSize: "2.1rem", fontWeight: 700, color: "#1a1f36" },
  subtitle: { margin: "0.25rem 0 0 0", color: "#64748b", fontSize: "0.95rem" },
  meta: { textAlign: "right", color: "#64748b", fontSize: "0.9rem", lineHeight: 1.5 },
  controls: {
    display: "flex",
    flexWrap: "wrap",
    gap: "12px",
    marginBottom: "1.5rem",
    alignItems: "flex-end",
  },
  searchInput: {
    padding: "10px 14px",
    minWidth: "260px",
    border: "1px solid #cbd5e1",
    borderRadius: "6px",
    fontSize: "0.95rem",
  },
  tableWrapper: {
    overflowX: "auto",
    borderRadius: "8px",
    border: "1px solid #e2e8f0",
  },
  table: { width: "100%", borderCollapse: "collapse", fontSize: "0.95rem" },
  th: {
    background: "#f8fafc",
    color: "#1e293b",
    fontWeight: 600,
    textAlign: "left",
    padding: "12px 14px",
    borderBottom: "2px solid #cbd5e1",
    whiteSpace: "nowrap",
  },
  td: { padding: "11px 14px", borderBottom: "1px solid #e2e8f0", verticalAlign: "middle" },
  amount: { textAlign: "right", fontFamily: "monospace", fontWeight: 500 },
  journalLink: { color: "#2563eb", cursor: "pointer", textDecoration: "none" },
  totalsRow: { background: "#f1f5f9", fontWeight: 700, color: "#0f172a" },
  noData: { padding: "4rem 1rem", textAlign: "center", color: "#64748b", fontSize: "1.1rem", fontStyle: "italic" },
  footer: { marginTop: "2.5rem", textAlign: "center", color: "#94a3b8", fontSize: "0.85rem" },
};

const headCells = [
  { id: "date", label: "Date", align: "left" },
  { id: "account", label: "Account", align: "left" },
  { id: "details", label: "Description", align: "left" },
  { id: "debit", label: "Debit", align: "right" },
  { id: "credit", label: "Credit", align: "right" },
  { id: "balance", label: "Balance", align: "right" },
  { id: "journal", label: "Journal #", align: "center" },
];

const GeneralLedger = ({ actions, isResolved }) => {
  const today = dateConvert(); // or new Date().toISOString().split('T')[0]

  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openingBalance, setOpeningBalance] = useState(0);
  const [search, setSearch] = useState("");
  const [dates, setDates] = useState({ from_date: today, to_date: today });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [openJournal, setOpenJournal] = useState(false);
  const [selectedJournal, setSelectedJournal] = useState("");

  const { handlePrint, componentRef } = usePrint();

  const excelConfig = {
    file_name: "General_Ledger",
    url: "accounting/general_ledger_excel?",
    params: { start_date: dates.from_date, end_date: dates.to_date, search },
  };

  const pdfConfig = {
    file_name: "General_Ledger",
    url: "accounting/general_ledger_pdf?",
    params: { start_date: dates.from_date, end_date: dates.to_date, search },
  };

  const { exportExcel, isLoading: excelLoading } = useExcelReports(actions, excelConfig);
  const { exportPDF, isLoading: pdfLoading } = usePdfReports(actions, pdfConfig);

  const fetchLedger = useCallback(() => {
    setLoading(true);

    axios
      .get(`${config.epharmUrl}/accounting/general_ledger_details`, {
        params: {
          start_date: dates.from_date,
          end_date: dates.to_date,
          page: page + 1,
          per_page: rowsPerPage,
          search: search.trim() || undefined,
        },
      })
      .then((res) => {
        const data = res?.data?.data?.[0] ?? {};
        setOpeningBalance(Number(data.openingbalance ?? 0));
        setTotalCount(Number(data.total_count ?? 0));

        const rows = (data.details ?? []).map((item, idx) => ({
          id: item.journal || `${page}-${idx}`,
          Date: item.Date,
          Account: item.Account || item.account || item.accountname || "—",
          details: item.details || item.description || "",
          Debit: Number(item.Debit ?? item.debit ?? 0),
          Credit: Number(item.Credit ?? item.credit ?? 0),
          balance: Number(item.balance ?? 0),
          journal: String(item.journal ?? item.journalID ?? item.journal_id ?? ""), // ← force string
        }));

        setLedger(rows);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Ledger fetch error:", err);
        actions.snackbarActions.showSnackBar({
          message: "Failed to load general ledger",
          variant: "error",
        });
        setLoading(false);
      });
  }, [dates.from_date, dates.to_date, page, rowsPerPage, search, actions]);

  useEffect(() => {
    fetchLedger();
  }, [fetchLedger, isResolved]);

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDates((prev) => ({ ...prev, [name]: value }));
    setPage(0);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenJournal = (journal) => {
    if (!journal) return;
    setSelectedJournal(journal);
    setOpenJournal(true);
  };

  const totalDebit = ledger.reduce((sum, r) => sum + r.Debit, 0);
  const totalCredit = ledger.reduce((sum, r) => sum + r.Credit, 0);

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>General Ledger</h1>
          <p style={styles.subtitle}>Transaction history with running balance</p>
        </div>
        <div style={styles.meta}>
          <div>
            Period: <strong>{dates.from_date}</strong> — <strong>{dates.to_date}</strong>
          </div>
          <div>Generated: {new Date().toLocaleString()}</div>
        </div>
      </div>

      {/* Controls */}
      <div style={styles.controls}>
        <AccountingDateFilter
          from_date={dates.from_date}
          to_date={dates.to_date}
          handleChange={handleDateChange}
          submitted={true}
          handleSubmit={fetchLedger}
          isPending={loading}
        />

        <input
          type="text"
          placeholder="Search account, description, journal..."
          value={search}
          onChange={handleSearchChange}
          style={styles.searchInput}
        />

        <button
          onClick={fetchLedger}
          disabled={loading}
          className="btn btn-primary btn-sm"
        >
          {loading ? "Loading..." : "Apply"}
        </button>

        {ledger.length > 0 && (
          <>
            <button
              onClick={exportPDF}
              disabled={pdfLoading || loading}
              className="btn btn-outline-secondary btn-sm"
            >
              {pdfLoading ? "Preparing..." : "PDF"}
            </button>
            <button
              onClick={exportExcel}
              disabled={excelLoading || loading}
              className="btn btn-outline-secondary btn-sm"
            >
              {excelLoading ? "Exporting..." : "Excel"}
            </button>
          </>
        )}
      </div>

      {/* Opening Balance */}
      <RightAlignedContainer style={{ margin: "1.5rem 0" }}>
        <strong style={{ fontSize: "1.2rem", color: "#1e293b" }}>
          Opening Balance: {formatAmount(openingBalance)}
        </strong>
      </RightAlignedContainer>

      {/* Journal Dialog */}
      <CustomDialog
        open={openJournal}
        handleClose={() => setOpenJournal(false)}
        title={`Journal Entry #${selectedJournal}`}
        maxWidth="lg"
      >
        <div style={{ marginBottom: 16, textAlign: "right" }}>
          <button onClick={handlePrint} className="btn btn-primary">
            Print
          </button>
        </div>
        <div ref={componentRef}>
          <SpecificJournalTemplate selectedJournalID={selectedJournal} />
        </div>
      </CustomDialog>

      {/* Table */}
      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr>
              {headCells.map((cell) => (
                <th
                  key={cell.id}
                  style={{ ...styles.th, textAlign: cell.align }}
                >
                  {cell.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} style={styles.noData}>
                  Loading ledger entries...
                </td>
              </tr>
            ) : ledger.length === 0 ? (
              <tr>
                <td colSpan={7} style={styles.noData}>
                  No transactions found for the selected period
                </td>
              </tr>
            ) : (
              <>
                {ledger.map((row) => (
                  <tr key={row.id} style={{ backgroundColor: row.id % 2 === 0 ? "#f9fafb" : "#ffffff" }}>
                    <td style={styles.td}>{formatDateTime(row.Date)}</td>
                    <td style={styles.td}>{row.Account}</td>
                    <td style={styles.td}>{row.details}</td>
                    <td style={{ ...styles.td, ...styles.amount }}>
                      {row.Debit > 0 ? formatDigits(row.Debit) : "—"}
                    </td>
                    <td style={{ ...styles.td, ...styles.amount }}>
                      {row.Credit > 0 ? formatDigits(row.Credit) : "—"}
                    </td>
                    <td style={{ ...styles.td, ...styles.amount, fontWeight: 600 }}>
                      {formatDigits(row.balance)}
                    </td>
                    <td style={{ ...styles.td, textAlign: "center" }}>
                      {row.journal ? (
                        <span
                          style={styles.journalLink}
                          role="button"
                          tabIndex={0}
                          onClick={() => handleOpenJournal(row.journal)}
                          onKeyDown={(e) => e.key === "Enter" && handleOpenJournal(row.journal)}
                        >
                          {row.journal}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                ))}

                <tr style={styles.totalsRow}>
                  <td style={{ ...styles.td, fontWeight: 700 }} colSpan={3}>
                    Period Totals
                  </td>
                  <td style={{ ...styles.td, ...styles.amount }}>
                    {formatDigits(totalDebit)}
                  </td>
                  <td style={{ ...styles.td, ...styles.amount }}>
                    {formatDigits(totalCredit)}
                  </td>
                  <td style={{ ...styles.td, ...styles.amount }} />
                  <td style={styles.td} />
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div style={{ marginTop: "1.5rem" }}>
        <MuiPagination
          rowsPerPage={rowsPerPage}
          page={page}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          total_count={totalCount}
          rows={[10, 20, 50, 100, 200, 500]}
          colSpan={headCells.length}
        />
      </div>

      {/* Footer */}
      <div style={styles.footer}>
        <div>Generated by {config.appName || "Your System"}</div>
        <div>© {new Date().getFullYear()}</div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({ snackbars: state.snackbars });
const mapDispatchToProps = (dispatch) => ({
  actions: { snackbarActions: bindActionCreators(snackbarActions, dispatch) },
});

export default connect(mapStateToProps, mapDispatchToProps)(GeneralLedger);